import type { GrowthPoint } from './growth-model';

export type GrowthAnomalyMetric = 'signups' | 'visitors';
export type GrowthAnomalyDirection = 'spike' | 'drop';

export type GrowthAnomaly = {
  timestamp: string;
  metric: GrowthAnomalyMetric;
  metricLabel: string;
  direction: GrowthAnomalyDirection;
  value: number;
  previousValue: number;
  baseline: number;
  percentFromBaseline: number | null;
};

type DetectGrowthAnomaliesOptions = {
  bucketMinutes: number;
  goalLabel?: string;
  now?: Date;
};

const median = (values: number[]): number => {
  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
};

const percentChange = (value: number, baseline: number): number | null =>
  baseline > 0 ? ((value - baseline) / baseline) * 100 : null;

const completedBucket = (
  timestamp: string,
  bucketMinutes: number,
  now: Date,
): boolean => Date.parse(timestamp) + bucketMinutes * 60_000 <= now.getTime();

const anomalyForMetric = (
  points: GrowthPoint[],
  index: number,
  metric: GrowthAnomalyMetric,
  metricLabel: string,
): GrowthAnomaly | null => {
  const value = points[index][metric];
  if (value === null || !Number.isFinite(value) || index < 2) {
    return null;
  }

  const previousValue = points[index - 1][metric];
  if (previousValue === null || !Number.isFinite(previousValue)) {
    return null;
  }
  const prior = points
    .slice(Math.max(0, index - 7), index)
    .map((point) => point[metric])
    .filter((item): item is number => item !== null && Number.isFinite(item));
  if (prior.length < 3) {
    return null;
  }
  const baseline = median(prior);

  const isSignup = metric === 'signups';
  const spikeMinimum = isSignup ? 5 : 20;
  const minimumChange = isSignup ? 5 : 10;
  const spikeRatio = isSignup ? 3 : 1.5;
  const previousRatio = isSignup ? 2 : 1.35;
  const spike =
    value >= spikeMinimum &&
    value - baseline >= minimumChange &&
    value >= Math.max(spikeMinimum, Math.max(1, baseline) * spikeRatio) &&
    value - previousValue >= minimumChange &&
    value >= Math.max(spikeMinimum, Math.max(1, previousValue) * previousRatio);

  const dropBaseline = isSignup ? 5 : 20;
  const drop =
    baseline >= dropBaseline &&
    baseline - value >= minimumChange &&
    previousValue - value >= minimumChange &&
    value <= baseline * 0.4;

  if (!spike && !drop) {
    return null;
  }

  return {
    timestamp: points[index].timestamp,
    metric,
    metricLabel,
    direction: spike ? 'spike' : 'drop',
    value,
    previousValue,
    baseline,
    percentFromBaseline: percentChange(value, baseline),
  };
};

/**
 * Flag completed buckets with a material change against both the previous
 * bucket and a robust recent baseline. AI never decides whether a point is an
 * anomaly; it only investigates points returned here.
 */
export const detectGrowthAnomalies = (
  points: GrowthPoint[],
  {
    bucketMinutes,
    goalLabel = 'Signups',
    now = new Date(),
  }: DetectGrowthAnomaliesOptions,
): GrowthAnomaly[] =>
  points.flatMap((point, index) => {
    if (!completedBucket(point.timestamp, bucketMinutes, now)) {
      return [];
    }
    const anomalies = [
      anomalyForMetric(points, index, 'signups', goalLabel),
      anomalyForMetric(points, index, 'visitors', 'Visitors'),
    ].filter((item): item is GrowthAnomaly => item !== null);
    return anomalies;
  });

export const growthBucketLabel = (bucketMinutes: number): string =>
  bucketMinutes < 1440
    ? 'hour'
    : bucketMinutes > 10080
      ? '4-week period'
      : bucketMinutes > 1440
        ? 'week'
        : 'day';

export const growthAnomalyPrompt = (
  anomaly: GrowthAnomaly,
  bucketMinutes: number,
): string => {
  const start = new Date(anomaly.timestamp);
  const inclusiveEnd = new Date(
    start.getTime() + bucketMinutes * 60_000 - 1,
  );
  const periodStart = start.toISOString().slice(0, 10);
  const periodEnd = inclusiveEnd.toISOString().slice(0, 10);

  return [
    'Investigate this analytics anomaly for the current project.',
    `Period (UTC, inclusive): ${periodStart} to ${periodEnd}`,
    `Metric: ${JSON.stringify(anomaly.metricLabel)}`,
    `Metric key: ${anomaly.metric}`,
    `Observed: ${anomaly.value}`,
    `Previous ${growthBucketLabel(bucketMinutes)}: ${anomaly.previousValue}`,
    `Recent median: ${anomaly.baseline}`,
    `Direction: ${anomaly.direction}`,
    'Use the exact anomaly period, its immediately preceding equal-length period, and the recent baseline. Do not use totals or attribution from the wider dashboard range to explain this period.',
    'Investigate traffic sources, referrers, campaigns, entry pages, Search Console, public mentions, and paid outcomes where available. Treat an entry page as the conversion location, not the acquisition cause. Never infer signup geography, browser, device, or referrer from generic traffic breakdowns. Server-side event metadata may describe the ingestion server rather than the registered user. Treat Direct as unattributed unless the goal evidence explicitly links the conversion to a browser visitor. Goal creation time is not evidence of when tracking began. Do not diagnose a tracking bug without direct event-quality evidence.',
    'Answer in 90 words or fewer. Use exactly: one conclusion paragraph; one **Confidence:** line; then an Evidence: heading with at most two bullets. Put supporting numbers and links only under Evidence. If no driver is isolated, say the cause is unknown. Do not offer a follow-up analysis that the available tools can perform now.',
  ].join('\n');
};
